import { defineStore } from "pinia";
import _ from "lodash";
import type { ProductsByIds } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { useWarehousesAndStores } from "./warehousesAndStores";
import { userStore } from "./user";

interface State {
	wishlistItems: number[];
	wishlistProducts: ProductsByIds | null;
}

export const useWishlistStore = defineStore({
	id: "wishListStore",
	state: (): State => {
		return {
			wishlistItems: [],
			wishlistProducts: null
		};
	},
	actions: {
		async updateWishList(value: number) {
			if (this.wishlistItems.includes(value)) {
				this.wishlistItems.splice(this.wishlistItems.indexOf(value), 1);
			} else {
				this.wishlistItems = [...this.wishlistItems, value];
			}

			await this.fetchWishlistProducts(this.wishlistItems);
			if (await useLoadUser().userHasSession()) {
				const userId = userStore()?.user?.Id;
				if (this.wishlistItems.length) {
					return await useNorcePwa().updateWishlist({
						wishlist: this.wishlistItems,
						userId: userId!
					});
				}

				return await useNorcePwa().deleteWishlist({
					userId: userId!
				});
			}
		},
		async initWishlist() {
			const userId = userStore()?.user?.Id;
			const wishlistCached = await useNorcePwa().getWishlist({
				userId: userId!
			});

			if (
				wishlistCached &&
				typeof wishlistCached === "string" &&
				wishlistCached?.length
			) {
				this.wishlistItems = _.uniq([
					...this.wishlistItems,
					...wishlistCached.split(",").map(Number)
				]);
			}

			await this.fetchWishlistProducts(this.wishlistItems);
		},
		async updateWishlistOnLogIn() {
			const userId = userStore()?.user?.Id;
			if (await useLoadUser().userHasSession()) {
				let wishlistCached = await useNorcePwa().getWishlist({
					userId: userId!
				});

				wishlistCached = wishlistCached?.toString();

				if (wishlistCached && wishlistCached.length) {
					const wishlistCachedArray = wishlistCached.split(",").map(Number);
					const updatedWishlist = _.uniq([
						...this.wishlistItems,
						...wishlistCachedArray
					]);

					await useNorcePwa().updateWishlist({
						wishlist: updatedWishlist,
						userId: userId!
					});
					this.wishlistItems = updatedWishlist;
				} else {
					await useNorcePwa().updateWishlist({
						wishlist: this.wishlistItems,
						userId: userId!
					});
				}

				await this.fetchWishlistProducts(this.wishlistItems);
			}
		},
		async fetchWishlistProducts(wishlistItems: number[]) {
			const locale = useNuxtApp().$i18n.locale.value;

			if (wishlistItems.length) {
				this.wishlistProducts = await useNorcePwa().listProductsByIds({
					idSeed: wishlistItems.join(","),
					asVariants: true,
					storeSeed: useWarehousesAndStores()?.getStores?.[locale]?.map(
						(store) => store.Id
					),
					cultureCode: useNorceStore().storeContext.cultureCode,
					currencyId: useNorceStore().storeContext.currencyId.toString()
				});
				this.wishlistProducts.ItemCount = this.wishlistProducts.Items.length;
			} else {
				this.wishlistProducts = null;
			}
		}
	},
	getters: {
		getWishlistItems: (state) => state.wishlistItems
	},
	persist: {
		storage: persistedState.localStorage,
		paths: ["wishlistItems", "wishlistProducts"]
	}
});
